import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import EmployerNavBar from "../../components/EmployerNavBar";
import PushStatusPanel from "../../components/PushStatusPanel";
import { statusClasses, ROLE_CLASSES } from "../../constants/statusColors";
import {
  Calendar,
  FileText,
  FlaskConical,
  MessageCircle,
  ClipboardList,
  Users,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";

const AVAILABILITY_LABELS = {
  immediate: "Available immediately",
  one_month: "Available in 1 month",
  three_months: "Available in 3 months",
  not_looking: "Not actively looking",
};

const AVAILABILITY_COLORS = {
  immediate: ROLE_CLASSES.active,
  one_month: "bg-brand-violet-tint/50 text-brand-violet dark:bg-brand-violet/10 dark:text-brand-violet-on-dark",
  three_months: ROLE_CLASSES.waiting,
  not_looking: ROLE_CLASSES.neutral,
};

const ENTRY_TYPE_LABELS = {
  experience: "Work experience",
  education: "Education",
  certification: "Certification",
  project: "Project",
  volunteer: "Volunteer work",
  achievement: "Achievement",
  publication: "Publication",
};

const DEFAULT_PROCESS_STEPS = [
  "Phone screen",
  "Technical assessment",
  "First interview",
  "Final interview",
  "Offer",
];

const SENT_STATUS_LABEL = {
  no_response: "No response",
  accepted: "Accepted",
  closed: "Closed by candidate",
};

const SENT_STATUS_CLASS = {
  no_response: ROLE_CLASSES.neutral,
  accepted: ROLE_CLASSES.positive,
  closed: ROLE_CLASSES.negative,
};

// ── Resume Modal ──────────────────────────────────────────────

function ResumeModal({ candidate, employerToken, onClose }) {
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [arenaBlocked, setArenaBlocked] = useState(false);
  const [arenaBlockedMessage, setArenaBlockedMessage] = useState("");

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    api
      .get(`/employer/arena/candidates/${candidate.id}/resume`, authConfig)
      .then(({ data }) => setResume(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [candidate.id]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Candidate resume
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {candidate.anonymous_handle ?? "Anonymous candidate"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-5 bg-gray-100 dark:bg-white/5 rounded w-1/2" />
              <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-3/4" />
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-4"
                >
                  <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-1/3" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-sm">Could not load resume.</p>
            </div>
          )}

          {!loading && !error && resume && (
            <>
              {!resume.has_resume ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-sm font-medium mb-1">
                    No public resume
                  </p>
                  <p className="text-gray-400 text-xs">
                    This candidate hasn't published a resume yet.
                  </p>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {resume.name}
                    </h3>
                    {resume.summary && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {resume.summary}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400">
                        Anonymous profile via
                      </span>
                      <Logo className="h-10 sm:h-12 flex-shrink-0" />
                    </div>
                  </div>

                  {/* Timeline entries grouped by type */}
                  {resume.entries.length === 0 ? (
                    <p className="text-sm text-gray-400">
                      No entries on this resume yet.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-6">
                      {(() => {
                        const order = [
                          "experience",
                          "education",
                          "certification",
                          "project",
                          "volunteer",
                          "achievement",
                          "publication",
                        ];
                        const groups = {};
                        for (const entry of resume.entries) {
                          if (!groups[entry.entry_type])
                            groups[entry.entry_type] = [];
                          groups[entry.entry_type].push(entry);
                        }
                        return order
                          .filter((t) => groups[t])
                          .map((type) => (
                            <section key={type}>
                              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                                {ENTRY_TYPE_LABELS[type] ?? type}
                              </h4>
                              <div className="flex flex-col gap-3">
                                {groups[type].map((entry) => (
                                  <div
                                    key={entry.id}
                                    className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-4 border border-gray-100 dark:border-white/10"
                                  >
                                    <div className="flex items-start justify-between gap-3 mb-1">
                                      <div>
                                        <p className="font-semibold text-sm text-gray-900 dark:text-white">
                                          {entry.title}
                                        </p>
                                        {entry.organisation && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {entry.organisation}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-2 flex-wrap">
                                      {entry.location && (
                                        <span>{entry.location}</span>
                                      )}
                                      {entry.started_at && (
                                        <span>
                                          {entry.location && "· "}
                                          {new Date(
                                            entry.started_at,
                                          ).getFullYear()}
                                          {entry.is_current
                                            ? " – Present"
                                            : entry.ended_at
                                              ? ` – ${new Date(entry.ended_at).getFullYear()}`
                                              : ""}
                                        </span>
                                      )}
                                    </div>
                                    {entry.description && (
                                      <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                        {entry.description}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </section>
                          ));
                      })()}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Send Job Panel ────────────────────────────────────────────

function SendJobPanel({ candidates, onClose, onSent, employerToken }) {
  const [mode, setMode] = useState("existing"); // "existing" | "freeText"
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [saveAsPosition, setSaveAsPosition] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    message: "",
    location: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "EUR",
    is_remote: false,
  });
  const [processSteps, setProcessSteps] = useState([
    "Phone screen",
    "Interview",
    "Offer",
  ]);
  const [newStep, setNewStep] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  // Load the employer's own active positions so they can pick one instead
  // of writing a free-text pitch. Only active positions make sense here —
  // sending a filled/closed one would dead-end the candidate immediately.
  useEffect(() => {
    api
      .get("/job-positions?status=active", authConfig)
      .then(({ data }) => {
        setPositions(data);
        // No positions to pick from — fall back to free text automatically
        // rather than showing an empty dropdown with nothing to select.
        if (data.length === 0) setMode("freeText");
      })
      .catch(() => setMode("freeText"))
      .finally(() => setPositionsLoading(false));
  }, []);

  const handleSelectPosition = (positionId) => {
    setSelectedPositionId(positionId);
    const position = positions.find((p) => p.id === positionId);
    if (!position) return;

    // Collapse the position's separate location fields into the single
    // free-text field EmployerJobPosition expects.
    const locationParts = [
      position.location_city,
      position.location_region,
      position.location_country,
    ].filter(Boolean);

    setForm({
      title: position.title,
      description: position.description ?? "",
      message: "",
      location: locationParts.join(", "),
      salary_min: position.salary_min?.toString() ?? "",
      salary_max: position.salary_max?.toString() ?? "",
      salary_currency: position.salary_currency ?? "EUR",
      is_remote: position.is_remote,
    });
    setProcessSteps(position.process_steps ?? []);
  };

  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleModeChange = (newMode) => {
    // Switching away from an existing-position selection with data already
    // typed in is a real "are you sure" moment — silently discarding a
    // filled form is the kind of thing that erodes trust in a tool.
    if (mode === "existing" && newMode === "freeText" && selectedPositionId) {
      setShowResetConfirm(true);
      return;
    }
    doModeChange(newMode);
  };

  const doModeChange = (newMode) => {
    setMode(newMode);
    setSelectedPositionId("");
    setSaveAsPosition(false);
    setForm({
      title: "",
      description: "",
      message: "",
      location: "",
      salary_min: "",
      salary_max: "",
      salary_currency: "EUR",
      is_remote: false,
    });
    setProcessSteps(["Phone screen", "Interview", "Offer"]);
    setShowResetConfirm(false);
  };

  const addStep = () => {
    if (!newStep.trim()) return;
    setProcessSteps([...processSteps, newStep.trim()]);
    setNewStep("");
  };

  const removeStep = (idx) =>
    setProcessSteps(processSteps.filter((_, i) => i !== idx));

  const moveStep = (idx, dir) => {
    const steps = [...processSteps];
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    [steps[idx], steps[target]] = [steps[target], steps[idx]];
    setProcessSteps(steps);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    // Declared before the try so the catch block can report how many
    // sends succeeded before a failure.
    let sentCount = 0;
    try {
      // Check every candidate before creating anything. Position creation
      // is a real, visible action (it shows up on the Positions page and
      // counts against the plan limit), so it shouldn't happen only to
      // have the actual pitch fail afterward.
      const alreadyConnected = candidates.filter((c) => c.already_connected);
      if (alreadyConnected.length > 0) {
        setError(
          alreadyConnected.length === candidates.length
            ? "All selected candidates are already connected to you. Manage them from Connected candidates instead."
            : `${alreadyConnected.length} of the selected candidates are already connected to you. Deselect them and try again.`,
        );
        setSaving(false);
        return;
      }
      let sourcePositionId =
        mode === "existing" ? selectedPositionId || null : null;

      // Save-as-position: create the real position first, so a plan-limit
      // rejection stops everything before any pitch is sent, and a bulk
      // send shares one position instead of creating one per candidate.
      if (mode === "freeText" && saveAsPosition) {
        // The pitch form has one free-text location; positions store city
        // and country separately. "Stockholm, Sweden" splits into both,
        // a single word is treated as the city.
        const parts = form.location
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        const { data: created } = await api.post(
          "/job-positions",
          {
            title: form.title.trim(),
            employment_type: "Full-time",
            is_remote: form.is_remote,
            location_city: parts.length > 0 ? parts[0] : null,
            location_country: parts.length > 1 ? parts[parts.length - 1] : null,
            salary_min: form.salary_min ? parseInt(form.salary_min) : null,
            salary_max: form.salary_max ? parseInt(form.salary_max) : null,
            salary_currency: form.salary_currency,
            description: form.description || null,
            process_steps: processSteps.length > 0 ? processSteps : null,
          },
          authConfig,
        );
        sourcePositionId = created.id;
      }

      const payload = {
        ...form,
        salary_min: form.salary_min ? parseInt(form.salary_min) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max) : null,
        process_steps: processSteps.length > 0 ? processSteps : null,
        message: form.message || null,
        source_position_id: sourcePositionId,
      };

      // Sequential rather than Promise.all, so a failure stops cleanly
      // with an accurate count.
      for (const candidate of candidates) {
        await api.post(
          "/employer/arena/job-positions",
          { arena_profile_id: candidate.id, ...payload },
          authConfig,
        );
        sentCount++;
      }
      onSent();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail ?? err.message;
      if (candidates.length > 1 && sentCount > 0) {
        setError(
          `Sent to ${sentCount} of ${candidates.length} before failing: ${detail}`,
        );
      } else {
        setError(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Send job position
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              To: {candidates.length === 1
                ? candidates[0].anonymous_handle ?? "Anonymous candidate"
                : `${candidates.length} candidates`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {showResetConfirm && (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-[60]"
              onClick={() => setShowResetConfirm(false)}
            />
            <div className="fixed inset-0 flex items-center justify-center z-[70] px-4">
              <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
                <p className="font-semibold text-gray-900 dark:text-white mb-1">
                  Discard prefilled details?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-5">
                  Switching to a new write-up will clear the fields filled in
                  from the selected position.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
                  >
                    Keep editing
                  </button>
                  <button
                    onClick={() => doModeChange("freeText")}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium transition"
                  >
                    Discard and start new
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Mode toggle */}
        {!positionsLoading && positions.length > 0 && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="flex gap-2 mb-1">
              <button
                type="button"
                onClick={() => handleModeChange("existing")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                  mode === "existing"
                    ? "bg-brand-violet text-white"
                    : "border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400"
                }`}
              >
                Use existing position
              </button>
              <button
                type="button"
                onClick={() => handleModeChange("freeText")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                  mode === "freeText"
                    ? "bg-brand-violet text-white"
                    : "border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400"
                }`}
              >
                Write new one
              </button>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          id="send-job-form"
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
        {mode === "freeText" && (
          <div className="bg-gray-50 dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 flex flex-col gap-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saveAsPosition}
                onChange={(e) => setSaveAsPosition(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-brand-violet focus:ring-brand-violet"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Also save this as a position
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              {saveAsPosition
                ? "A position will be created from these details. It will appear on your Positions page and be listed in the Arena for job seekers, and candidates who accept will join its pipeline."
                : "This sends a one-off pitch to this candidate only. It won't appear on your Positions page or be findable by other candidates in the Arena."}
            </p>
          </div>
        )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {mode === "existing" && (
            <div>
              <label className={labelClass}>Position *</label>
              <select
                required
                value={selectedPositionId}
                onChange={(e) => handleSelectPosition(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a position...</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Fields below are prefilled from this position and can still be
                adjusted before sending.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Personal message to candidate</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="e.g. Hi, we came across your profile and think you'd be a great fit..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
            <p className="text-xs text-gray-400 mt-1">
              This is the first thing the candidate sees
            </p>
          </div>

          <div>
            <label className={labelClass}>Job title *</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Senior Frontend Engineer"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Job description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Describe the role, responsibilities, requirements..."
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div>
            <label className={labelClass}>Location</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Stockholm, Sweden"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Salary range</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={form.salary_min}
                onChange={(e) =>
                  setForm({ ...form, salary_min: e.target.value })
                }
                placeholder="Min"
                className={inputClass}
              />
              <input
                type="number"
                value={form.salary_max}
                onChange={(e) =>
                  setForm({ ...form, salary_max: e.target.value })
                }
                placeholder="Max"
                className={inputClass}
              />
              <select
                value={form.salary_currency}
                onChange={(e) =>
                  setForm({ ...form, salary_currency: e.target.value })
                }
                className={`${inputClass} w-24`}
              >
                {["EUR", "SEK", "GBP", "USD", "NOK", "DKK"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_remote}
              onChange={(e) =>
                setForm({ ...form, is_remote: e.target.checked })
              }
              className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            Remote position
          </label>

          <div>
            <label className={labelClass}>Expected hiring process</label>
            <p className="text-xs text-gray-400 mb-2">
              Shown to the candidate as a progress pipeline
            </p>
            <div className="flex flex-col gap-1.5 mb-2">
              {processSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 bg-gray-50 dark:bg-brand-dark-bg rounded-lg px-3 py-2"
                >
                  <span className="w-5 h-5 rounded-full bg-brand-violet/20 text-brand-violet dark:text-brand-violet-on-dark text-xs flex items-center justify-center font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                    {step}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveStep(idx, -1)}
                      disabled={idx === 0}
                      className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(idx, 1)}
                      disabled={idx === processSteps.length - 1}
                      className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      className="text-gray-300 hover:text-red-500 transition ml-1"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addStep();
                  }
                }}
                placeholder="Add step..."
                className={inputClass}
              />
              <button
                type="button"
                onClick={addStep}
                className="flex-shrink-0 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark px-3 py-2 rounded-lg text-sm hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition"
              >
                Add
              </button>
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2">
              {DEFAULT_PROCESS_STEPS.filter(
                (s) => !processSteps.includes(s),
              ).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setProcessSteps([...processSteps, s])}
                  className="text-xs text-gray-400 border border-gray-200 dark:border-white/10 px-2 py-0.5 rounded-full hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="send-job-form"
            disabled={saving}
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving
              ? "Sending..."
              : candidates.length > 1
                ? `Send to ${candidates.length} candidates`
                : "Send position"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Candidate Card ────────────────────────────────────────────

function CandidateCard({ candidate, selected, onToggleSelect, onSend, onViewResume }) {
  const skills = candidate.skills_snapshot ?? [];

  return (
    <div
      className={`bg-white dark:bg-brand-dark-surface rounded-2xl border p-5 flex flex-col gap-4 hover:shadow-sm transition ${
        selected
          ? "border-brand-violet dark:border-brand-violet-on-dark ring-1 ring-brand-violet/30 dark:ring-brand-violet-on-dark/30"
          : "border-gray-100 dark:border-white/10"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(candidate.id)}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-violet focus:ring-brand-violet flex-shrink-0"
          />
          <div className="w-10 h-10 rounded-xl bg-brand-violet/20 flex items-center justify-center text-brand-violet dark:text-brand-violet-on-dark font-bold text-lg flex-shrink-0">
            ?
          </div>
          <div>
            <p className="font-semibold text-sm text-gray-900 dark:text-white">
              {candidate.anonymous_handle ?? "Anonymous candidate"}
            </p>
            {candidate.location_preference && (
              <p className="text-xs text-gray-400 mt-0.5">
                {candidate.location_preference}
              </p>
            )}
          </div>
        </div>
        {candidate.availability && (
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 ${
              AVAILABILITY_COLORS[candidate.availability] ??
              "bg-gray-100 text-gray-500"
            }`}
          >
            {AVAILABILITY_LABELS[candidate.availability] ??
              candidate.availability}
          </span>
        )}
      </div>

      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {skills.slice(0, 6).map((s, i) => (
            <span
              key={i}
              className="text-xs bg-brand-violet-tint dark:bg-brand-violet/20 text-brand-violet dark:text-brand-violet-on-dark px-2.5 py-0.5 rounded-full"
            >
              {s.skill}
              {s.years ? ` · ${s.years}yr` : ""}
            </span>
          ))}
          {skills.length > 6 && (
            <span className="text-xs text-gray-400 px-2.5 py-0.5">
              +{skills.length - 6} more
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          {candidate.salary_min && (
            <span>
              {candidate.salary_min.toLocaleString()}–
              {candidate.salary_max?.toLocaleString()}{" "}
              {candidate.salary_currency ?? "EUR"}
            </span>
          )}
          {candidate.open_to_relocation && (
            <span className="text-teal-500">Open to relocation</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewResume(candidate)}
            className="border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            View resume
          </button>
          <button
            onClick={() => onSend([candidate])}
            className="bg-brand-violet text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 transition"
          >
            Send job position
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sent Positions List ───────────────────────────────────────

function SentPositionsList({ positions }) {
  if (positions.length === 0)
    return (
      <p className="text-sm text-gray-400 text-center py-6">
        No positions sent yet.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      {positions.map((p) => (
        <div
          key={p.id}
          className="flex items-center justify-between gap-4 p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-xl"
        >
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {p.title}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              {p.location && (
                <span className="text-xs text-gray-400">{p.location}</span>
              )}
              {p.salary_min && (
                <span className="text-xs text-gray-400">
                  · {p.salary_min.toLocaleString()}–
                  {p.salary_max?.toLocaleString()} {p.salary_currency}
                </span>
              )}
              <span className="text-xs text-gray-400">
                · {new Date(p.created_at).toLocaleDateString("en-SE")}
              </span>
            </div>
          </div>
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 ${
              SENT_STATUS_CLASS[p.status] ?? SENT_STATUS_CLASS.no_response
            }`}
          >
            {SENT_STATUS_LABEL[p.status] ?? "No response"}
          </span>
        </div>
      ))}
    </div>
  );
}

const ACTION_ICONS = {
  meeting: Calendar,
  document_request: FileText,
  assessment: FlaskConical,
  info: MessageCircle,
};

function CandidateHistoryPanel({ candidate, employerToken, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    api
      .get(
        `/employer/connected-candidates/${candidate.application_id}/history`,
        authConfig,
      )
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory({ statuses: [] }))
      .finally(() => setLoading(false));
  }, [candidate.application_id]);

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString("en-SE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Process history
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {candidate.job_title} · {candidate.company}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="flex flex-col gap-4 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-gray-100 dark:bg-white/5 rounded-xl"
                />
              ))}
            </div>
          )}

          {!loading && history?.statuses.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-12">
              No status history yet.
            </p>
          )}

          {!loading && history?.statuses.length > 0 && (
            <div className="flex flex-col gap-6">
              {history.statuses.map((s, i) => (
                <div key={s.id} className="flex gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${i === 0 ? "bg-brand-violet" : "bg-gray-300 dark:bg-gray-600"}`}
                    />
                    {i < history.statuses.length - 1 && (
                      <div className="w-px flex-1 min-h-8 bg-gray-200 dark:bg-white/10 mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusClasses(s.status)}`}
                      >
                        {s.status}
                      </span>
                      <span
                        className={`text-xs font-medium ${s.source === "employer" ? "text-brand-violet dark:text-brand-violet-on-dark" : "text-gray-400"}`}
                      >
                        {s.source === "employer" ? "via you" : "by candidate"}
                      </span>
                    </div>
                    {s.note && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {s.note}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      {formatDate(s.created_at)}
                    </p>

                    {/* Actions */}
                    {s.actions.length > 0 && (
                      <div className="flex flex-col gap-2 mt-3">
                        {s.actions.map((a) => {
                          const IconComponent =
                            ACTION_ICONS[a.action_type] ?? ClipboardList;
                          return (
                            <div
                              key={a.id}
                              className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-3 border border-gray-100 dark:border-white/10"
                            >
                              <div className="flex items-start gap-2">
                                <IconComponent className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                                    {a.title}
                                  </p>
                                  {a.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {a.description}
                                    </p>
                                  )}
                                  {a.meeting_at && (
                                    <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark mt-0.5 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(a.meeting_at)}
                                      {a.duration_minutes &&
                                        ` · ${a.duration_minutes} min`}
                                    </p>
                                  )}
                                  {a.attendees?.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {a.attendees.join(", ")}
                                    </p>
                                  )}
                                  {a.deadline && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Deadline: {formatDate(a.deadline)}
                                    </p>
                                  )}
                                  {a.response && (
                                    <span
                                      className={`inline-flex items-center gap-1 text-xs font-medium mt-1 px-2 py-0.5 rounded-full ${
                                        a.response === "accepted" ||
                                        a.response === "submitted"
                                        ? ROLE_CLASSES.positive
                                        : ROLE_CLASSES.negative
                                      }`}
                                    >
                                      {a.response === "accepted" && (
                                        <CheckCircle className="w-3 h-3" />
                                      )}
                                      {a.response === "declined" && (
                                        <XCircle className="w-3 h-3" />
                                      )}
                                      {a.response === "submitted" && (
                                        <CheckCircle className="w-3 h-3" />
                                      )}
                                      {a.response}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function EmployerArena() {
  const navigate = useNavigate();
  const { employerToken, employerUser, clearEmployerAuth } = useAuthStore();

  const [candidates, setCandidates] = useState([]);
  const [sentPositions, setSentPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  const [sentSuccess, setSentSuccess] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [arenaBlocked, setArenaBlocked] = useState(false);
  const [arenaBlockedMessage, setArenaBlockedMessage] = useState("");
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [connectedCandidates, setConnectedCandidates] = useState([]);
  const [historyCandidate, setHistoryCandidate] = useState(null);
  const [pushCandidate, setPushCandidate] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saveAsPosition, setSaveAsPosition] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const [filters, setFilters] = useState({
    role: "",
    skill: "",
    availability: "",
    location: "",
    salary_max: "",
    relocation: false,
  });

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    document.title = employerUser?.is_agency
      ? "Joborigo — Agency Dashboard"
      : "Joborigo — Employer Dashboard";
  }, [employerUser]);

  const fetchCandidates = async (currentFilters = filters) => {
    setLoading(true);
    setArenaBlocked(false);
    try {
      const params = new URLSearchParams();
      if (currentFilters.skill) params.set("skill", currentFilters.skill);
      if (currentFilters.availability)
        params.set("availability", currentFilters.availability);
      if (currentFilters.location)
        params.set("location", currentFilters.location);
      if (currentFilters.salary_max)
        params.set("salary_max", currentFilters.salary_max);
      if (currentFilters.relocation) params.set("open_to_relocation", "true");

      const { data } = await api.get(
        `/employer/arena/candidates?${params.toString()}`,
        authConfig,
      );

      let result = data;
      if (currentFilters.role) {
        const roleLower = currentFilters.role.toLowerCase();
        result = data.filter((c) =>
          c.anonymous_handle?.toLowerCase().includes(roleLower),
        );
      }
      setCandidates(result);
    } catch (err) {
      if (err.response?.status === 403) {
        setArenaBlocked(true);
        setArenaBlockedMessage(
          err.response.data?.detail ?? "Upgrade your plan to access the arena.",
        );
        setCandidates([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSentPositions = async () => {
    try {
      const { data } = await api.get(
        "/employer/arena/sent-positions",
        authConfig,
      );
      setSentPositions(data);
    } catch {}
  };

  const fetchConnectedCandidates = async () => {
    try {
      const { data } = await api.get(
        "/employer/connected-candidates",
        authConfig,
      );
      setConnectedCandidates(data);
    } catch {}
  };

  const fetchConnectionStatus = async () => {
    try {
      const { data } = await api.get(
        "/employer/connection-status",
        authConfig,
      );
      setConnectionStatus(data);
    } catch {}
  };

  useEffect(() => {
    fetchCandidates();
    fetchSentPositions();
    fetchConnectedCandidates();
    fetchConnectionStatus();
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/employer/auth/logout", {}, authConfig);
    } catch {}
    clearEmployerAuth();
    navigate("/employers/login");
  };

  const handleFilterChange = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value }));
  const handleSearch = (e) => {
    e.preventDefault();
    fetchCandidates();
  };
  const handleClearFilters = () => {
    const cleared = {
      role: "",
      skill: "",
      availability: "",
      location: "",
      salary_max: "",
      relocation: false,
    };
    setFilters(cleared);
    fetchCandidates(cleared);
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-1">
            {employerUser?.is_agency ? "Agency Arena" : "Candidate Arena"}
          </h1>
          <p className="text-gray-400 text-sm">
            {employerUser?.is_agency
              ? "Browse candidates and manage your placements."
              : "Browse anonymous candidates. No names or gender shown."}
          </p>
        </div>

        <div className="flex gap-2 mb-6">
          {[
            { value: "browse", label: "Browse candidates" },
            {
              value: "sent",
              label: `Sent positions${sentPositions.length > 0 ? ` (${sentPositions.length})` : ""}`,
            },
            {
              value: "connected",
              label: `Connected${connectedCandidates.length > 0 ? ` (${connectedCandidates.length})` : ""}`,
            },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeTab === tab.value
                  ? "bg-brand-violet text-white"
                  : "bg-white dark:bg-brand-dark-surface text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "sent" && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
              Sent job positions
            </h2>
            <SentPositionsList positions={sentPositions} />
          </div>
        )}

        {activeTab === "connected" && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
              Connected candidates
            </h2>
            {connectionStatus?.at_limit && connectionStatus.skipped > 0 && (
              <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-700/30 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {connectionStatus.skipped} applicant
                  {connectionStatus.skipped !== 1 ? "s aren't" : " isn't"}{" "}
                  listed here because you've reached your plan's limit of{" "}
                  {connectionStatus.max} connected candidates. They are still in
                  your position pipelines, where you can push status to them.
                </p>
                <a
                  href="/employers/settings"
                  className="inline-block mt-2 text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline"
                >
                  View upgrade options
                </a>
              </div>
            )}
            {connectedCandidates.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                No connected candidates yet. Connect a candidate from your
                dashboard.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {connectedCandidates.map((c) => (
                  <div
                    key={c.integration_id}
                    className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-xl"
                  >
                    <div className="flex-1 min-w-0">
                      {c.candidate_name && (
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5 truncate">
                          {c.candidate_name}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {c.job_title}
                        </p>
                        <span className="text-gray-400 text-xs">·</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {c.company}
                        </p>
                        {c.candidate_ref && (
                          <span className="text-xs text-gray-400 font-mono">
                            ref: {c.candidate_ref}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark">
                          {c.current_status}
                        </span>
                        {c.last_push_at ? (
                          <span className="text-xs text-gray-400">
                            Last push:{" "}
                            {new Date(c.last_push_at).toLocaleDateString(
                              "en-SE",
                            )}{" "}
                            · {c.last_push_status}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">
                            No pushes yet
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {c.push_count} push{c.push_count !== 1 ? "es" : ""}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        Connected{" "}
                        {new Date(c.connected_at).toLocaleDateString("en-SE")}
                      </span>
                      <button
                        onClick={() => setHistoryCandidate(c)}
                        className="text-xs border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-3 py-1.5 rounded-lg hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                      >
                        View process
                      </button>
                      <button
                        onClick={() => setPushCandidate(c)}
                        className="text-xs bg-brand-violet text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition"
                      >
                        Push update
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "browse" && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filter panel */}
            <div className="w-full lg:w-64 flex-shrink-0">
              <form
                onSubmit={handleSearch}
                className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5 flex flex-col gap-4 lg:sticky lg:top-24"
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">
                    Filters
                  </p>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                  >
                    Clear all
                  </button>
                </div>

                {[
                  {
                    key: "role",
                    label: "Job role",
                    placeholder: "e.g. Frontend Engineer",
                    note: "Matches candidate handle",
                  },
                  {
                    key: "skill",
                    label: "Skill",
                    placeholder: "e.g. Python, React",
                  },
                  {
                    key: "location",
                    label: "Location preference",
                    placeholder: "e.g. Nordics, Remote",
                  },
                ].map(({ key, label, placeholder, note }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      {label}
                    </label>
                    <input
                      value={filters[key]}
                      onChange={(e) => handleFilterChange(key, e.target.value)}
                      placeholder={placeholder}
                      className={inputClass}
                    />
                    {note && (
                      <p className="text-xs text-gray-400 mt-1">{note}</p>
                    )}
                  </div>
                ))}

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Availability
                  </label>
                  <select
                    value={filters.availability}
                    onChange={(e) =>
                      handleFilterChange("availability", e.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">Any</option>
                    <option value="immediate">Immediately</option>
                    <option value="one_month">Within 1 month</option>
                    <option value="three_months">Within 3 months</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    Max salary expectation
                  </label>
                  <input
                    type="number"
                    value={filters.salary_max}
                    onChange={(e) =>
                      handleFilterChange("salary_max", e.target.value)
                    }
                    placeholder="e.g. 80000"
                    className={inputClass}
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={filters.relocation}
                    onChange={(e) =>
                      handleFilterChange("relocation", e.target.checked)
                    }
                    className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                  />
                  Open to relocation only
                </label>

                <button
                  type="submit"
                  className="bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
                >
                  Search candidates
                </button>
              </form>
            </div>

            {/* Results */}
            <div className="flex-1 min-w-0">
              {sentSuccess && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3 flex items-center justify-between">
                  <p className="text-green-700 dark:text-green-400 text-sm font-medium">
                    Job position sent successfully!
                  </p>
                  <button
                    onClick={() => setSentSuccess(false)}
                    className="text-green-600 text-xs hover:underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              <p className="text-sm text-gray-400 mb-4">
                {loading
                  ? "Searching..."
                  : `${candidates.length} candidate${candidates.length !== 1 ? "s" : ""} found`}
              </p>

              {loading ? (
                <div className="flex flex-col gap-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5 animate-pulse"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5" />
                        <div>
                          <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-48 mb-1" />
                          <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-24" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : arenaBlocked ? (
                <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-brand-violet/30 dark:border-brand-violet-on-dark/30 p-16 text-center">
                  <p className="text-brand-violet dark:text-brand-violet-on-dark text-sm font-semibold mb-1">
                    Upgrade required
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mb-4 max-w-sm mx-auto">
                    {arenaBlockedMessage}
                  </p>
                  <a
                    href="/employers/settings"
                    className="inline-block bg-brand-violet text-white px-5 py-2 rounded-lg text-xs font-medium hover:opacity-90 transition"
                  >
                    View upgrade options
                  </a>
                </div>
              ) : candidates.length === 0 ? (
                <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-dashed border-gray-200 dark:border-white/10 p-16 text-center">
                  <p className="text-gray-400 text-sm font-medium mb-1">
                    No candidates found
                  </p>
                  <p className="text-gray-400 text-xs mb-3">
                    Try adjusting your filters or check back as more seekers
                    join.
                  </p>
                  <button
                    onClick={handleClearFilters}
                    className="text-brand-violet dark:text-brand-violet-on-dark text-xs hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  {selectedIds.size > 0 && (
                    <div className="mb-4 flex items-center justify-between bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/30 dark:border-brand-violet-on-dark/30 rounded-xl px-4 py-3">
                      <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark">
                        {selectedIds.size} candidate{selectedIds.size !== 1 ? "s" : ""} selected
                      </p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={clearSelection}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                        >
                          Clear
                        </button>
                        <button
                          onClick={() =>
                            setSelectedCandidate(
                              candidates.filter((c) => selectedIds.has(c.id)),
                            )
                          }
                          className="bg-brand-violet text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 transition"
                        >
                          Send job position to selected
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    {candidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        selected={selectedIds.has(candidate.id)}
                        onToggleSelect={toggleSelect}
                        onSend={setSelectedCandidate}
                        onViewResume={setResumeCandidate}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {selectedCandidate && selectedCandidate.length > 0 && (
        <SendJobPanel
          candidates={selectedCandidate}
          employerToken={employerToken}
          onClose={() => setSelectedCandidate(null)}
          onSent={() => {
            setSentSuccess(true);
            fetchSentPositions();
            clearSelection();
          }}
        />
      )}

      {resumeCandidate && (
        <ResumeModal
          candidate={resumeCandidate}
          employerToken={employerToken}
          onClose={() => setResumeCandidate(null)}
        />
      )}
      {historyCandidate && (
        <CandidateHistoryPanel
          candidate={historyCandidate}
          employerToken={employerToken}
          onClose={() => setHistoryCandidate(null)}
        />
      )}
      {pushCandidate && (
        <PushStatusPanel
          applicationId={pushCandidate.application_id}
          subtitle={`${pushCandidate.job_title} · ${pushCandidate.company}`}
          employerToken={employerToken}
          onClose={() => setPushCandidate(null)}
          onPushed={() => fetchConnectedCandidates()}
        />
      )}
    </div>
  );
}
